import {keepPreviousData, useQuery} from "@tanstack/react-query";

import {getPagination} from "@/src/modules/core/lib";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";

import {permanentParkingService} from "../services";

type UsePermanentParkingPermitsParams = {
  rows: number;
  page: number;
  filters?: Record<string, unknown>;
  sort?: string;
  enabled?: boolean;
};

export function usePermanentParkingPermits({
  rows,
  page,
  filters = {},
  sort = "-created_at",
  enabled = true,
}: UsePermanentParkingPermitsParams) {
  const userProperty = useStoredUserProperty();
  const {property_id, context_property_id, ...rest} = filters as {
    property_id?: number;
    context_property_id?: number;
    [k: string]: unknown;
  };

  const filter: Record<string, string | number> = {
    context_property_id: context_property_id ?? property_id ?? userProperty?.id ?? 0,
    ...Object.fromEntries(
      Object.entries(rest).filter(
        ([, v]) =>
          v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0),
      ) as [string, string | number][],
    ),
  };

  const options = {
    page: {limit: rows, offset: (page - 1) * rows},
    filter,
    sort,
  };

  const query = useQuery({
    queryKey: ["permanentparkingpermits", options],
    queryFn: () => permanentParkingService.getPermits(options),
    retry: false,
    enabled,
    placeholderData: keepPreviousData,
  });

  const permits = query.isSuccess && query.data ? query.data.data : [];
  const totalRecords = query.data?.meta?.total ?? 0;
  const {start, end} = getPagination(totalRecords, rows, page);

  return {
    permits,
    totalRecords,
    start,
    end,
    ...query,
    isPending: query.isPending && enabled,
  };
}
